import { Button, Card, CardContent, Grid, Popover, Stack, Tooltip, Typography } from '@mui/material'
import React, { useMemo, useState } from 'react'
import useTitle from 'utils/customHook/useTitle'
import i18n from 'simple-react-i18n'
import { CardTitle } from 'components/card/NewCard'
import PropTypes from 'prop-types'
import { Circle, CircleOutlined, ErrorOutline, Tune } from '@mui/icons-material'
import { round, orderBy, without, includes, compact, values, template } from 'lodash'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { formatSign, hasValue } from 'utils/NumberUtil'
import A3PCheckbox from './A3PComponent/A3PCheckbox'
import { push } from '@lagunovsky/redux-react-router'
import PopoverContent from './A3PComponent/PopoverContent'
import { getHydroSituationColor, getLevelLabel, getTrendIcon, getTrendLabel, trendEvolution } from 'exploitations/utils/AgriUtils'
import { CardTable } from 'components/datatable/NewTable'
import { getDarkerHex } from 'utils/ColorUtil'
import { blackColor, darkBlue, whiteColor } from 'utils/constants/ColorTheme'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import SummaryIndicator from './A3PComponent/SummaryIndicator'
import { getRiskOfShortageLabel, getSitu, groupAlertLevels } from '../utils/A3PUtils'
import RecommendationIndicator from './A3PComponent/RecommendationIndicator'
import VPIndicator from './A3PComponent/VPIndicator'
import { RISK_OF_SHORTAGE, VP_LEVELS } from 'territory/constants/A3PConstants'
import useGlobalParameter from 'utils/customHook/useGlobalParameter'

const NO_RESTRICTION = -1
const BG_ORANGE = '#ffe9cd'

const WhiteGrid = ({
    size = 'auto',
    title,
    style = {},
    children,
}) => (
    <Grid
        container
        size={size}
        direction={'column'}
        justifyContent={'space-between'}
        sx={{
            padding: 1,
            borderRadius: 2,
            backgroundColor: whiteColor,
            ...style,
        }}
    >
        <Typography sx={{ fontSize: '13px' }}>{title}</Typography>
        <Grid container spacing={0.5} alignItems={'center'}>
            {children}
        </Grid>
    </Grid>
)

WhiteGrid.propTypes = {
    children: PropTypes.node,
    title: PropTypes.string,
    style: PropTypes.shape({}),
    size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

const HeaderComponent = ({ title, popover, popoverWidth = '250px' }) => {
    const [anchorPopover, setAnchorPopover] = useState(null)
    return (
        <Grid
            container
            size={12}
            spacing={0.5}
            color={'white'}
            alignItems={'center'}
            justifyContent={'space-between'}
            wrap={'nowrap'}
        >
            {popover ?
                <ErrorOutline
                    onClick={event => {
                        event.stopPropagation()
                        setAnchorPopover(anchorPopover ? null : event.currentTarget)
                    }}
                    sx={{
                        fontSize: '1.3rem',
                        '&:hover': {
                            cursor: 'pointer',
                        },
                    }}
                />
                :
                undefined
            }
            <Grid
                size={'grow'}
                sx={{
                    minHeight: '2.4rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: 12,
                    lineHeight: 1.2,
                    whiteSpace: 'normal',
                    overflowWrap: 'normal',
                    wordBreak: 'normal',
                    textAlign: 'center',
                }}
            >
                {title}
            </Grid>
            <Popover
                open={!!anchorPopover}
                anchorEl={anchorPopover}
                onClose={(event) => {
                    event.stopPropagation()
                    setAnchorPopover(null)
                }}
                onClick={(event) => event.stopPropagation()}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 2,
                        },
                    },
                }}
            >
                <Grid container size={12} sx={{ padding: 1.5, width: popoverWidth }}>
                    {popover}
                    <Grid
                        container
                        size={12}
                        justifyContent={'flex-end'}
                    >
                        <Button
                            onClick={() => setAnchorPopover(null)}
                            variant={'text'}
                            sx={{ textTransform: 'capitalize' }}
                        >
                            {i18n.close}
                        </Button>
                    </Grid>
                </Grid>
            </Popover>
        </Grid>
    )
}

HeaderComponent.propTypes = {
    title: PropTypes.string,
    popover: PropTypes.element,
    popoverWidth: PropTypes.string,
}

const TerritoryA3PDashboard = () => {
    const [dryScenario, setDryScenario] = useState(false)
    const [normalScenario, setNormalScenario] = useState(false)
    const [dampScenario, setDampScenario] = useState(false)
    const [anchorClimatePopover, setAnchorClimatePopover] = useState(null)
    const [anchorFilterPopover, setAnchorFilterPopover] = useState(null)
    const [VPLevelsFilters, setVPLevelsFilters] = useState([])
    const [trajDeviationFilters, setTrajDeviationFilter] = useState('')
    const [alertLevelsFilters, setAlertLevelsFilters] = useState([])
    const [filters, setFilters] = useState({
        VPLevels: [], // Values from 0 to 3,
        trajDeviation: '', // 'neg' || 'pos' || 'all' || ''
        alertLevels: [], // contains restrictions IDs from alertLevels object
    })

    const {
        A3PDetails: {
            nbUgInRiskOfShortage,
            alertLevels = [],
            recommendedModif = {},
            climate,
            managementUnitsDetails = [],
        },
    } = useSelector(store => ({
        A3PDetails: store.AgriReducer.A3PDetails,
    }), shallowEqual)

    const dispatch = useDispatch()

    const nbMoisClimato = useGlobalParameter('A3P_nb_mois', v => v ? Number(v) : undefined)

    const showClimateControlScenarioPanel = useApplicationSetting('showClimateControlScenarioPanel', v => v === '1' ? 'block' : 'none')
    const restrictedVPLevelLabel = useApplicationSetting('restrictedVPLevelLabel', v => hasValue(v) ? String(v) : null)
    const normalVPLevelLabel = useApplicationSetting('normalVPLevelLabel', v => hasValue(v) ? String(v) : null)
    const highVPLevelLabel = useApplicationSetting('highVPLevelLabel', v => hasValue(v) ? String(v) : null)
    const veryHighVPLevelLabel = useApplicationSetting('veryHighVPLevelLabel', v => hasValue(v) ? String(v) : null)

    useTitle(() => [{
        title: i18n.steering,
        href: 'territory',
    }, {
        title: i18n.A3P,
        href: 'territory/A3P',
    }], [])

    const alertLevelGroups = useMemo(() => groupAlertLevels(alertLevels), [alertLevels])

    const filteredManagementUnitsDetails = useMemo(() => compact(managementUnitsDetails.filter(managementUnitDetail => {
        const formatedManagementUnitDetail = {
            ...managementUnitDetail,
            waterDispo: {
                ...managementUnitDetail.waterDispo,
                underground: {
                    ...managementUnitDetail.waterDispo?.underground,
                    restrictionId: managementUnitDetail.waterDispo?.underground?.restrictionId ?? NO_RESTRICTION,
                },
                superficial: {
                    ...managementUnitDetail.waterDispo?.superficial,
                    restrictionId: managementUnitDetail.waterDispo?.superficial?.restrictionId ?? NO_RESTRICTION,
                },
            },
        }
        const VPLevelFilterOK = filters.VPLevels.length ?
            includes(filters.VPLevels, formatedManagementUnitDetail.withdrawableVolume)
            :
            true
        if (!VPLevelFilterOK) return undefined

        const trajDeviationFilterOK = !(filters.trajDeviation && filters.trajDeviation !== 'all') ||
            (filters.trajDeviation === 'neg' ?
                formatedManagementUnitDetail.consumptionTrajectoryDeviation < 0
                :
                formatedManagementUnitDetail.consumptionTrajectoryDeviation >= 0
            )
        if (!trajDeviationFilterOK) return undefined

        const alertLevelsFilterOK = filters.alertLevels.length ?
            includes(filters.alertLevels, formatedManagementUnitDetail.waterDispo.underground.restrictionId) ||
            includes(filters.alertLevels, formatedManagementUnitDetail.waterDispo.superficial.restrictionId)
            :
            true
        if (!alertLevelsFilterOK) return undefined

        return managementUnitDetail
    })), [filters, managementUnitsDetails])

    const formattedManagementUnitsDetails = useMemo(() => filteredManagementUnitsDetails.map(ugDetails => {
        const vpValue = ugDetails.vpLevelValues?.[ugDetails.withdrawableVolume]
        const estimatedNeed = ugDetails.iaeauResults?.graphe_thermometre?.besoin_total_estime
        return ({
            ...ugDetails,
            coverageRateOfNeeds: hasValue(vpValue) && estimatedNeed > 0 ? (vpValue / estimatedNeed) * 100 : undefined,
        })
    }), [filteredManagementUnitsDetails])

    const rows = useMemo(() => {
        return formattedManagementUnitsDetails.map(managementUnitDetail => {
            const { underground, superficial } = managementUnitDetail.waterDispo ?? {}
            const situSout = underground && getSitu(underground)
            const situSup = superficial && getSitu(superficial)
            const hasPercentageConsumed = hasValue(managementUnitDetail.percentageAutorisationConsumed)
            const hasPercentageMateriels = hasValue(managementUnitDetail.percentageMaterielsWithEntries)
            const hasTrajDeviation = hasValue(managementUnitDetail.consumptionTrajectoryDeviation)
            const hasCoverageRate = hasValue(managementUnitDetail.coverageRateOfNeeds)
            return ({
                managementUnit: managementUnitDetail.managementUnitName,
                surfaceWater: {
                    sortValue: superficial?.resultClass ?? NO_RESTRICTION,
                    value: situSup,
                    align: 'center',
                },
                undergroundWater: {
                    sortValue: underground?.resultClass ?? NO_RESTRICTION,
                    value: situSout,
                    align: 'center',
                },
                percentVPConsumed: {
                    sortValue: managementUnitDetail.percentageAutorisationConsumed ?? -1,
                    align: 'center',
                    value: (
                        <Grid container justifyContent='center'>
                            <Tooltip title={hasPercentageMateriels ? `${i18n.indexCaptureRate} : ${managementUnitDetail.percentageMaterielsWithEntries}%` : ''}>
                                <Typography>{hasPercentageConsumed ? `${round(managementUnitDetail.percentageAutorisationConsumed)} %` : '-'}</Typography>
                            </Tooltip>
                        </Grid>
                    ),
                },
                consumptionDeviation: {
                    sortValue: managementUnitDetail.consumptionTrajectoryDeviation,
                    value: hasTrajDeviation ? `${formatSign(round(managementUnitDetail.consumptionTrajectoryDeviation, 1))} %` : '-',
                    align: 'center',
                    fontColor: managementUnitDetail.consumptionTrajectoryDeviation <= -10 ?
                        'validStatus.contrastText'
                        :
                        managementUnitDetail.consumptionTrajectoryDeviation > 10 ?
                            'errorStatus.contrastText'
                            :
                            undefined,
                },
                coverageNeeds: {
                    sortValue: managementUnitDetail.coverageRateOfNeeds ?? -1,
                    value: hasCoverageRate ? `${round(managementUnitDetail.coverageRateOfNeeds, 1)} %` : '-',
                    align: 'center',
                    fontColor: managementUnitDetail.coverageRateOfNeeds >= 100 ?
                        'validStatus.contrastText'
                        :
                        managementUnitDetail.coverageRateOfNeeds < 80 ?
                            'errorStatus.contrastText'
                            :
                            undefined,
                },
                riskOfShortage: {
                    sortValue: managementUnitDetail.riskOfShortage ?? -1,
                    value: getRiskOfShortageLabel(managementUnitDetail.riskOfShortage),
                    align: 'center',
                    fontColor: managementUnitDetail.riskOfShortage > RISK_OF_SHORTAGE.UNLIKELY ?
                        'errorStatus.contrastText'
                        :
                        undefined,
                },
                withdrawableVolume: {
                    sortValue: managementUnitDetail.withdrawableVolume,
                    value:
                        <Grid container padding={1} justifyContent={'center'}>
                            <VPIndicator indicator={managementUnitDetail.withdrawableVolume} />
                        </Grid>,
                },
                advise: {
                    sortValue: managementUnitDetail.recommendation,
                    value: <Grid margin={1}><RecommendationIndicator indicator={managementUnitDetail.recommendation} /></Grid>,
                    align: 'center',
                },
                onClickValue: managementUnitDetail.managementCode,
            })
        })
    }, [formattedManagementUnitsDetails])

    return (
        <Stack spacing={1.5}>
            <Card sx={{ display: showClimateControlScenarioPanel }}>
                <CardTitle title={i18n.climateControlScenario} actions={[
                    <ErrorOutline
                        key={0}
                        onClick={event => {
                            event.stopPropagation()
                            setAnchorClimatePopover(anchorClimatePopover ? null : event.currentTarget)
                        }}
                        sx={{
                            fontSize: '25px',
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                    />]}
                />
                <Popover
                    open={!!anchorClimatePopover}
                    anchorEl={anchorClimatePopover}
                    onClose={(event) => {
                        event.stopPropagation()
                        setAnchorClimatePopover(null)
                    }}
                    onClick={(event) => event.stopPropagation()}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    slotProps={{
                        paper: {
                            sx: {
                                borderRadius: '16px',
                            },
                        },
                    }}
                >
                    <Grid container size={12} sx={{ padding: 1.5, width: '250px' }}>
                        <PopoverContent explaination={i18n.climateControlScenarioExplaination}/>
                        <Grid
                            container
                            size={12}
                            justifyContent={'flex-end'}
                        >
                            <Button
                                onClick={() => setAnchorClimatePopover(null)}
                                variant={'text'}
                                sx={{ textTransform: 'capitalize' }}
                            >
                                {i18n.close}
                            </Button>
                        </Grid>
                    </Grid>
                </Popover>
                <CardContent>
                    <Grid container size={12} alignItems={'center'} spacing={1.5}>
                        <A3PCheckbox label={i18n.dry} checked={dryScenario} onChange={isChecked => setDryScenario(isChecked)} />
                        <A3PCheckbox label={i18n.normal} checked={normalScenario} onChange={isChecked => setNormalScenario(isChecked)} />
                        <A3PCheckbox label={i18n.damp} checked={dampScenario} onChange={isChecked => setDampScenario(isChecked)} />
                    </Grid>
                </CardContent>
            </Card>
            <Card>
                <CardTitle title={i18n.indicatorsByManagementUnitSynthesis} />
                <CardContent>
                    <Grid container size={12} alignItems={'stretch'} spacing={1.5}>
                        <SummaryIndicator
                            size={{ xs: 4, xl: 2 }}
                            title={i18n.UGAtRiskOfShortage}
                            content={
                                <Grid container alignItems={'baseline'} spacing={0}>
                                    <Typography sx={{ fontSize: '40px', fontWeight: 'bold' }}>{nbUgInRiskOfShortage ?? '-'}</Typography>
                                    <Typography sx={{ fontSize: '25px' }}>{`/${managementUnitsDetails.length}`}</Typography>
                                </Grid>
                            }
                        />
                        <SummaryIndicator
                            size={{ xs: 4, xl: 2.5 }}
                            title={i18n.recommendedChanges}
                            secondaryContent={
                                <Grid container size={12} spacing={1}>
                                    <WhiteGrid
                                        size={6}
                                        title={i18n.reduct}
                                        style={{
                                            border: `1px solid ${blackColor}`,
                                            backgroundColor: recommendedModif.reduce > 0 ? BG_ORANGE : whiteColor,
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '20px', fontWeight: 'bold' }}>{recommendedModif.reduce ?? '-'}</Typography>
                                    </WhiteGrid>
                                    <WhiteGrid
                                        size={6}
                                        title={i18n.increase}
                                        style={{
                                            border: `1px solid ${blackColor}`,
                                            backgroundColor: recommendedModif.increase > 0 ? BG_ORANGE : whiteColor,
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '20px', fontWeight: 'bold' }}>{recommendedModif.increase ?? '-'}</Typography>
                                    </WhiteGrid>
                                </Grid>
                            }
                        />
                        <SummaryIndicator
                            size={{ xs: 4, xl: 2.5 }}
                            title={i18n.climate}
                            subTitle={hasValue(nbMoisClimato) ? template(i18n.observedOverTheLastXMonths)({ nbMonths: nbMoisClimato }) : undefined}
                            content={climate}
                        />
                        <SummaryIndicator
                            size={{ xs: 12, xl: 5 }}
                            title={i18n.warningsLevel}
                            secondaryContent={
                                <Grid container size={12} spacing={1}>
                                    {alertLevelGroups.map(alertGroup => (
                                        <WhiteGrid
                                            key={alertGroup.key}
                                            size={12 / alertLevelGroups.length}
                                            title={alertGroup.label}
                                        >
                                            {alertGroup.color ?
                                                <Circle sx={{ color: alertGroup.color }} />
                                                :
                                                <CircleOutlined />
                                            }
                                            <Grid size={'grow'} sx={{ fontSize: 20, fontWeight: 'bold' }}>
                                                {alertGroup.nbUg}
                                            </Grid>
                                        </WhiteGrid>
                                    ))}
                                </Grid>
                            }
                        />
                    </Grid>
                </CardContent>
            </Card>
            <CardTable
                title={i18n.detailByUG}
                actions={[
                    <Button
                        key={0}
                        onClick={(event) => {
                            event.stopPropagation()
                            setAnchorFilterPopover(anchorFilterPopover ? null : event.currentTarget)
                        }}
                        variant={'outlined'}
                        color='mainBlack'
                        sx={{ textTransform: 'capitalize', padding: '2px 8px' }}
                        startIcon={<Tune />}
                    >{i18n.filters}</Button>,
                ]}
                maxHeight={'calc(100vh - 298px)'}
                onClickRow={(row) => dispatch(push(`/territory/A3P/${row.onClickValue}`))}

                rows={rows}
                headers={[
                    {
                        key: 'managementUnit',
                        color: darkBlue,
                        paddingBottom: 1,
                        sticky: 'left',
                        value: <HeaderComponent title={i18n.managementUnit} />,
                    },
                    {
                        key: 'surfaceWater',
                        color: darkBlue,
                        paddingBottom: 1,
                        width: { xs: 'auto', xl: '180px' },
                        value: <HeaderComponent
                            title={i18n.surfaceWater}
                            popover={
                                <PopoverContent
                                    explaination={i18n.basedOnVCN3}
                                    legend={{
                                        keys: [
                                            i18n.trend,
                                            i18n.level,
                                        ],
                                        label: [
                                            <Stack key={0} direction={'row'} spacing={1}>
                                                {values(trendEvolution).map((label, i) => (
                                                    <Grid
                                                        key={i}
                                                        container
                                                        size='grow'
                                                        alignItems='center'
                                                        justifyContent='center'
                                                        spacing={1}
                                                        sx={{
                                                            borderRadius: 1,
                                                            backgroundColor: `${getHydroSituationColor('grey')}16`,
                                                            padding: 0.75,
                                                            fontSize: '11px',
                                                        }}
                                                    >
                                                        <Grid container size='auto'>{getTrendIcon(label, 'grey')}</Grid>
                                                        <Grid
                                                            container
                                                            size='grow'
                                                            alignItems={'center'}
                                                            justifyContent={'center'}
                                                        >
                                                            <Typography color={getDarkerHex(getHydroSituationColor('grey'), 25)}>
                                                                {getTrendLabel(label)}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                ))}
                                            </Stack>,
                                            <Grid container size={12} key={1} spacing={1}>
                                                {['darkblue', 'yellow', 'deepskyblue', 'orange', 'darkgreen', 'red', 'limegreen'].map((color, i) => (
                                                    <Grid
                                                        key={i}
                                                        container
                                                        size={6}
                                                        alignItems='center'
                                                        justifyContent='center'
                                                        spacing={1}
                                                        sx={{
                                                            borderRadius: 1,
                                                            backgroundColor: `${getHydroSituationColor(color)}16`,
                                                            padding: 0.75,
                                                            fontSize: '11px',
                                                        }}
                                                    >
                                                        <Grid container size='auto'>{getTrendIcon('right', color)}</Grid>
                                                        <Grid
                                                            container
                                                            size='grow'
                                                            alignItems={'center'}
                                                            justifyContent={'center'}
                                                        >
                                                            <Typography color={getDarkerHex(getHydroSituationColor(color), 25)}>
                                                                {getLevelLabel(color)}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                ))}
                                            </Grid>,
                                        ],
                                    }}
                                />
                            }
                            popoverWidth={'360px'}
                        />,
                    },
                    {
                        key: 'undergroundWater',
                        color: darkBlue,
                        paddingBottom: 1,
                        width: { xs: 'auto', xl: '180px' },
                        value: <HeaderComponent
                            title={i18n.undergroundWater}
                            popover={
                                <PopoverContent
                                    explaination={i18n.basedOnIPS}
                                    legend={{
                                        keys: [
                                            i18n.trend,
                                            i18n.level,
                                        ],
                                        label: [
                                            <Stack key={0} direction={'row'} spacing={1}>
                                                {values(trendEvolution).map((label, i) => (
                                                    <Grid
                                                        key={i}
                                                        container
                                                        size='grow'
                                                        alignItems='center'
                                                        justifyContent='center'
                                                        spacing={1}
                                                        sx={{
                                                            borderRadius: 1,
                                                            backgroundColor: `${getHydroSituationColor('grey')}16`,
                                                            padding: 0.75,
                                                            fontSize: '11px',
                                                        }}
                                                    >
                                                        <Grid container size='auto'>{getTrendIcon(label, 'grey')}</Grid>
                                                        <Grid
                                                            container
                                                            size='grow'
                                                            alignItems={'center'}
                                                            justifyContent={'center'}
                                                        >
                                                            <Typography
                                                                color={getDarkerHex(getHydroSituationColor('grey'), 25)}
                                                            >
                                                                {getTrendLabel(label)}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                ))}
                                            </Stack>,
                                            <Grid container size={12} key={1} spacing={1}>
                                                {['#4400F6', '#F7FF28', '#3E66ED', '#F6BD1B', '#34FFFB', '#F21007', '#48FF24'].map((color, i) => (
                                                    <Grid
                                                        key={i}
                                                        container
                                                        size={6}
                                                        alignItems='center'
                                                        justifyContent='center'
                                                        spacing={1}
                                                        sx={{
                                                            borderRadius: 1,
                                                            backgroundColor: `${color}16`,
                                                            padding: 0.75,
                                                            fontSize: '11px',
                                                        }}
                                                    >
                                                        <Grid container size='auto'>{getTrendIcon('right', color)}</Grid>
                                                        <Grid
                                                            container
                                                            size='grow'
                                                            alignItems={'center'}
                                                            justifyContent={'center'}
                                                        >
                                                            <Typography
                                                                color={getDarkerHex(color, 25)}
                                                            >
                                                                {getLevelLabel(color)}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                ))}
                                            </Grid>,
                                        ],
                                    }}
                                />
                            }
                            popoverWidth={'360px'}
                        />,
                    },
                    {
                        key: 'percentVPConsumed',
                        color: darkBlue,
                        paddingBottom: 1,
                        value: <HeaderComponent
                            title={i18n.percentVPConsumed}
                            popover={
                                <PopoverContent
                                    explaination={i18n.percentVPConsumedExplainationText}
                                />
                            }
                        />,
                    },
                    {
                        key: 'consumptionDeviation',
                        color: darkBlue,
                        paddingBottom: 1,
                        value: <HeaderComponent
                            title={i18n.consumptionDeviation}
                            popover={
                                <PopoverContent
                                    explaination={i18n.consumptionDeviationExplainationText}
                                    legend={{
                                        keys: [
                                            { label: i18n.belowMinus10Percent, color: 'validStatus.contrastText' },
                                            i18n.fromMinus10To5Percent,
                                            i18n.from5To10Percent,
                                            { label: i18n.moreThan10Percent, color: 'errorStatus.contrastText' },
                                        ],
                                        label: [
                                            i18n.consoLowerThanForecast,
                                            i18n.consoCloseToForecast,
                                            i18n.consoHigherThanForecast,
                                            i18n.consoSignificantlyHigherThanForecast,
                                        ],
                                    }}
                                />
                            }
                        />,
                    },
                    {
                        key: 'coverageNeeds',
                        color: darkBlue,
                        paddingBottom: 1,
                        value: <HeaderComponent
                            title={i18n.coverageNeeds}
                            popover={
                                <PopoverContent
                                    explaination={i18n.coverageNeedsExplainationText}
                                    legend={{
                                        keys: [
                                            { label: i18n.superiorOrEqualTo100Percent, color: 'validStatus.contrastText' },
                                            i18n.from80To99Percent,
                                            { label: i18n.below80Percent, color: 'errorStatus.contrastText' },
                                        ],
                                        label: [
                                            i18n.needsFullyCoveredOrExceeded,
                                            i18n.resourcesPartiallyInsufficientRequireVigilance,
                                            i18n.resourcesSignificantlyInsufficientHighRiskOfDeficit,
                                        ],
                                    }}
                                />
                            }
                        />,
                    },
                    {
                        key: 'riskOfShortage',
                        color: darkBlue,
                        paddingBottom: 1,
                        width: '150px',
                        value: <HeaderComponent
                            title={i18n.probOfExceedingThresholdShort}
                            popover={
                                <PopoverContent
                                    explaination={i18n.probOfExceedingThresholdExplainationText}
                                    legend={{
                                        keys: [
                                            i18n.unknown,
                                            i18n.veryUnlikely,
                                            i18n.unlikely,
                                            { label: i18n.likely, color: 'errorStatus.contrastText' },
                                            { label: i18n.veryLikely, color: 'errorStatus.contrastText' },
                                        ],
                                        label: [
                                            i18n.notEnoughShortageDatas,
                                            i18n.noRiskIdentified,
                                            i18n.lowRiskConfortableSituation,
                                            i18n.highRiskNearCriticalThreshold,
                                            i18n.thresholdExceededExpectedShortage,
                                        ],
                                    }}
                                />
                            }
                        />,
                    },
                    {
                        key: 'withdrawableVolume',
                        color: darkBlue,
                        paddingBottom: 1,
                        value: <HeaderComponent
                            title={i18n.withdrawableVolume}
                            popover={
                                <PopoverContent
                                    explaination={i18n.VPExplainationText}
                                    legend={{
                                        keys: [
                                            <VPIndicator key={VP_LEVELS.RESTRICTED} indicator={VP_LEVELS.RESTRICTED} withVolume />,
                                            <VPIndicator key={VP_LEVELS.NORMAL} indicator={VP_LEVELS.NORMAL} withVolume />,
                                            <VPIndicator key={VP_LEVELS.HIGH} indicator={VP_LEVELS.HIGH} withVolume />,
                                            <VPIndicator key={VP_LEVELS.VERY_HIGH} indicator={VP_LEVELS.VERY_HIGH} withVolume />,
                                        ],
                                        label: [
                                            i18n.restrictedVPExplaination,
                                            i18n.normalVPExplaination,
                                            i18n.highVPExplaination,
                                            i18n.veryHighVPExplaination,
                                        ],
                                    }}
                                />
                            }
                            popoverWidth='300px'
                        />,
                    },
                    {
                        key: 'advise',
                        color: darkBlue,
                        paddingBottom: 1,
                        value: <HeaderComponent title={i18n.advise} popover={<PopoverContent explaination={i18n.adviseExplainationText} />} />,
                    },
                ]}
            />
            <Popover
                open={!!anchorFilterPopover}
                anchorEl={anchorFilterPopover}
                onClose={(event) => {
                    event.stopPropagation()
                    setAnchorFilterPopover(null)
                }}
                onClick={(event) => event.stopPropagation()}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: '16px',
                        },
                    },
                }}
            >
                <Grid container size={12} sx={{ padding: 1.5, width: '600px' }}>
                    <PopoverContent legend={{
                        keys: [
                            i18n.VPLevel,
                            i18n.trajDeviation,
                            i18n.warningsLevel,
                        ],
                        label: [
                            <Grid key={0} container size={12} spacing={1} paddingBottom={1}>
                                <A3PCheckbox
                                    label={restrictedVPLevelLabel}
                                    sx={{ padding: 1 }}
                                    onChange={(isChecked) => {
                                        setVPLevelsFilters((prev) =>
                                            isChecked ?
                                                [ ...prev, VP_LEVELS.RESTRICTED ]
                                                :
                                                without(prev, VP_LEVELS.RESTRICTED),
                                        )
                                    }}
                                    checked={includes(VPLevelsFilters, VP_LEVELS.RESTRICTED)}
                                />
                                <A3PCheckbox
                                    label={normalVPLevelLabel}
                                    sx={{ padding: 1 }}
                                    onChange={(isChecked) => {
                                        setVPLevelsFilters((prev) =>
                                            isChecked ?
                                                [ ...prev, VP_LEVELS.NORMAL ]
                                                :
                                                without(prev, VP_LEVELS.NORMAL),
                                        )
                                    }}
                                    checked={includes(VPLevelsFilters, VP_LEVELS.NORMAL)}
                                />
                                <A3PCheckbox
                                    label={highVPLevelLabel}
                                    sx={{ padding: 1 }}
                                    onChange={(isChecked) => {
                                        setVPLevelsFilters((prev) =>
                                            isChecked ?
                                                [ ...prev, VP_LEVELS.HIGH ]
                                                :
                                                without(prev, VP_LEVELS.HIGH),
                                        )
                                    }}
                                    checked={includes(VPLevelsFilters, VP_LEVELS.HIGH)}
                                />
                                <A3PCheckbox
                                    label={veryHighVPLevelLabel}
                                    sx={{ padding: 1 }}
                                    onChange={(isChecked) => {
                                        setVPLevelsFilters((prev) =>
                                            isChecked ?
                                                [ ...prev, VP_LEVELS.VERY_HIGH ]
                                                :
                                                without(prev, VP_LEVELS.VERY_HIGH),
                                        )
                                    }}
                                    checked={includes(VPLevelsFilters, VP_LEVELS.VERY_HIGH)}
                                />
                            </Grid>,
                            <Grid key={1} container size={12} spacing={1} paddingBottom={1}>
                                <A3PCheckbox
                                    label={i18n.negative}
                                    sx={{ padding: 1 }}
                                    onChange={(isChecked) => setTrajDeviationFilter((prev) => {
                                        if (isChecked) {
                                            switch (prev) {
                                                case 'pos':
                                                    return 'all'
                                                case '': default:
                                                    return 'neg'
                                            }
                                        }
                                        switch (prev) {
                                            case 'all':
                                                return 'pos'
                                            case 'neg': default:
                                                return ''
                                        }
                                    })}
                                    checked={trajDeviationFilters === 'neg' || trajDeviationFilters === 'all'}
                                />
                                <A3PCheckbox
                                    label={i18n.positive}
                                    sx={{ padding: 1 }}
                                    onChange={(isChecked) => setTrajDeviationFilter((prev) => {
                                        if (isChecked) {
                                            switch (prev) {
                                                case 'neg':
                                                    return 'all'
                                                case '': default:
                                                    return 'pos'
                                            }
                                        }
                                        switch (prev) {
                                            case 'all':
                                                return 'neg'
                                            case 'pos': default:
                                                return ''
                                        }
                                    })}
                                    checked={trajDeviationFilters === 'pos' || trajDeviationFilters === 'all'}
                                />
                            </Grid>,
                            <Grid key={2} container size={12} spacing={1}>
                                {orderBy(alertLevels, 'idAlert', 'asc').map((restriction, i) => (
                                    <A3PCheckbox
                                        key={i}
                                        label={restriction.alertLabel}
                                        sx={{ padding: 1 }}
                                        onChange={(isChecked) => {
                                            setAlertLevelsFilters((prev) =>
                                                isChecked ?
                                                    [ ...prev, restriction.idAlert ]
                                                    :
                                                    without(prev, restriction.idAlert),
                                            )
                                        }}
                                        checked={includes(alertLevelsFilters, restriction.idAlert)}
                                    />
                                ))}
                            </Grid>,
                        ],
                    }}
                    />
                    <Grid container size={12} justifyContent={'flex-end'} spacing={1}>
                        <Button
                            onClick={() => {
                                setVPLevelsFilters([])
                                setTrajDeviationFilter('')
                                setAlertLevelsFilters([])
                            }}
                            variant={'outlined'}
                            sx={{ textTransform: 'capitalize' }}
                        >
                            {i18n.reinit}
                        </Button>
                        <Button
                            onClick={() => {
                                setFilters({
                                    VPLevels: VPLevelsFilters,
                                    trajDeviation: trajDeviationFilters,
                                    alertLevels: alertLevelsFilters,
                                })
                                setAnchorFilterPopover(null)
                            }}
                            variant={'contained'}
                            sx={{ textTransform: 'capitalize' }}
                        >
                            {i18n.apply}
                        </Button>
                    </Grid>
                </Grid>
            </Popover>
        </Stack>
    )
}

export default TerritoryA3PDashboard